import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useParams } from "react-router-dom";
import { getApiUrl } from "../config/api";

type Platform = "ozon" | "wildberries" | "yandex" | string;

type Store = {
  id: string;
  platform: Platform;
  name: string;
  isActive?: boolean;
};

const StorePage: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const [store, setStore] = useState<Store | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authHeaders = useMemo(() => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    } as Record<string, string>;
  }, []);

  const getPlatformBadgeClasses = (platform: Platform) => {
    if (platform === "ozon") return "bg-blue-100 text-blue-700 border-blue-200";
    if (platform === "wildberries")
      return "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200";
    if (platform === "yandex")
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    return "bg-gray-100 text-gray-700 border-gray-200";
  };

  const loadStore = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(getApiUrl("/api/stores"), { headers: authHeaders });
      if (!resp.ok) throw new Error("Не удалось загрузить магазины");
      const data = (await resp.json()) as Store[];
      const found = (data || []).find((s) => s.id === storeId) || null;
      setStore(found);
    } catch (e: any) {
      setError(e?.message || "Ошибка загрузки магазина");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, storeId]);

  useEffect(() => {
    loadStore();
  }, [loadStore]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          {store ? (
            <div className="flex items-center gap-2">
              <span
                className={`inline-block text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${getPlatformBadgeClasses(
                  store.platform
                )}`}
              >
                {store.platform}
              </span>
              <h1 className="text-2xl font-bold">{store.name}</h1>
            </div>
          ) : (
            <h1 className="text-2xl font-bold">Магазин</h1>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Tabs">
          <NavLink
            to="reviews"
            end={false}
            className={({ isActive }) =>
              `whitespace-nowrap py-2 border-b-2 text-sm font-medium ${
                isActive
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300"
              }`
            }
          >
            Отзывы
          </NavLink>
          <NavLink
            to="products"
            className={({ isActive }) =>
              `whitespace-nowrap py-2 border-b-2 text-sm font-medium ${
                isActive
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300"
              }`
            }
          >
            Товары
          </NavLink>
        </nav>
      </div>

      {error ? <div className="text-red-600">{error}</div> : null}
      {!store && loading ? <div>Загрузка...</div> : null}

      <div className="pt-2">
        <Outlet />
      </div>
    </div>
  );
};

export default StorePage;


